import { useState } from 'react';
import {Flame, Wind} from 'lucide-react';

export default function InteractiveGWPComparison() {
  const [methaneAmount, setMethaneAmount] = useState(1);
  
  const co2Equivalent = methaneAmount * 28;
  const n2oEquivalent = methaneAmount * 265;

  return (
    <div className="bg-white rounded-2xl p-8 border border-gray-200">
      <h3 className="text-xl font-bold text-gray-900 mb-6">
        Interactive GWP Comparison Tool
      </h3>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Amount of Methane (CH₄): <span className="text-[#2E8B57] font-bold">{methaneAmount} ton{methaneAmount !== 1 ? 's' : ''}</span>
        </label>
        <input
          type="range"
          min="0.1"
          max="10"
          step="0.1"
          value={methaneAmount}
          onChange={(e) => setMethaneAmount(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#2E8B57]"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0.1 ton</span>
          <span>10 tons</span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Methane */}
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-600" />
              <span className="font-semibold text-gray-900">Methane (CH₄)</span>
            </div>
            <span className="text-sm text-gray-600">{methaneAmount.toFixed(1)} tons</span>
          </div>
          <div className="bg-gray-100 rounded-lg overflow-hidden h-12">
            <div
              className="bg-orange-500 h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
              style={{ width: `${(methaneAmount / 10) * 100}%` }}
            >
              <span className="text-white font-semibold text-sm">GWP: 28</span>
            </div>
          </div>
        </div>

        {/* CO2 Equivalent */}
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-[#2E8B57]" />
              <span className="font-semibold text-gray-900">CO₂ Equivalent</span>
            </div>
            <span className="text-sm text-gray-600">{co2Equivalent.toFixed(1)} tons CO₂e</span>
          </div>
          <div className="bg-gray-100 rounded-lg overflow-hidden h-12">
            <div
              className="bg-[#2E8B57] h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
              style={{ width: `${Math.min((co2Equivalent / 100) * 100, 100)}%` }}
            >
              <span className="text-white font-semibold text-sm">= {co2Equivalent.toFixed(1)} tons CO₂</span>
            </div>
          </div>
        </div>

        {/* N2O Comparison */}
        <div className="relative opacity-70">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">If this were N₂O</span>
            </div>
            <span className="text-sm text-gray-600">{n2oEquivalent.toFixed(1)} tons CO₂e</span>
          </div>
          <div className="bg-gray-100 rounded-lg overflow-hidden h-12">
            <div
              className="bg-blue-600 h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
              style={{ width: '100%' }}
            >
              <span className="text-white font-semibold text-sm">GWP: 265 🔥</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
        <p className="text-sm text-gray-700">
          <strong>Result:</strong> Releasing {methaneAmount.toFixed(1)} ton{methaneAmount !== 1 ? 's' : ''} of methane has the same warming effect as releasing <strong className="text-[#2E8B57]">{co2Equivalent.toFixed(1)} tons of CO₂</strong> over 100 years.
        </p>
      </div>
    </div>
  );
}
